import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import './schedule-pages.css';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, customerApi, scheduleApi } from '../api/endpoints.ts';
import { ApiRequestError } from '../api/client.ts';
import { useSession } from '../state/session.tsx';
import { invalidateQuoteViews } from '../state/quote-cache.ts';
import { PlanRequiredPage, usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, ConfirmDialog, LoadingBlock, Select, TextArea, classes } from '../components/ui.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { useSeriesVisits } from '../hooks/use-series-visits.ts';
import { AppointmentBadge } from '../components/schedule/appointment-badge.tsx';
import { AppointmentActions } from '../components/schedule/appointment-actions.tsx';
import { describeRule, todayInTimezone } from '../components/schedule/format.ts';
import { formatLocalTime, formatShortDate } from '../i18n/datetime.ts';
import {
  RuleFields,
  firstVisitSelection,
  ruleStateFromSeries,
  ruleToApiFields,
  ruleWithPick,
  type RuleFormState,
} from '../components/schedule/rule-fields.tsx';
import { AssigneePicker } from '../components/schedule/assignee-picker.tsx';
import { AvailabilityPicker } from '../components/schedule/availability-picker.tsx';
import { BillingFields, type BillingFormState } from '../components/payments/billing-fields.tsx';
import { HorizonCard } from '../components/schedule/horizon-card.tsx';
import { PriceChangeCard } from '../components/schedule/price-change-card.tsx';
import { PaymentBadge } from '../components/payments/payment-badge.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { dayPriceSummary } from '@shared/schedule-day-pricing.ts';
import { useChecklistTemplateOptions } from '../components/schedule/use-checklist-template-options.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { getDateLocale } from '../i18n/locale.ts';
import type { AppointmentSeries, SeriesContract } from '@shared/types/index.ts';

/**
 * The agreement this schedule runs under, copied from the accepted quote at
 * booking. Read-only here: what the office needs at a glance is the term, the
 * renewal date — flagged when it is close enough to act on — and the clauses
 * that decide what renewal means. Only the clauses actually set are shown,
 * the same rule the customer's own quote page applies.
 */
function ContractCard({ contract }: { contract: SeriesContract }) {
  const { t } = useTranslation();

  // "Soon" is 60 days: inside most cancellation-notice windows, so the flag
  // shows while there is still time to renegotiate rather than after.
  const renewsSoon =
    contract.renewsOn !== null &&
    (() => {
      const days = (new Date(`${contract.renewsOn}T12:00:00Z`).getTime() - Date.now()) / 86_400_000;
      return days >= 0 && days <= 60;
    })();

  return (
    <section className="card mb-6" aria-labelledby="contract-heading">
      <h2 id="contract-heading" className="mb-3 text-lg font-semibold">
        {t('seriesDetail.contract.heading')}
      </h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-[color:var(--color-muted)]">{t('seriesDetail.contract.started')}</dt>
        <dd>{formatShortDate(contract.startedOn)}</dd>

        <dt className="text-[color:var(--color-muted)]">{t('seriesDetail.contract.term')}</dt>
        <dd>
          {contract.termMonths === null
            ? t('seriesDetail.contract.monthToMonth')
            : t('seriesDetail.contract.termMonths', { count: contract.termMonths })}
        </dd>

        {contract.renewsOn !== null ? (
          <>
            <dt className="text-[color:var(--color-muted)]">{t('seriesDetail.contract.renewsOn')}</dt>
            <dd className={renewsSoon ? 'font-bold' : undefined}>
              {formatShortDate(contract.renewsOn)}
              {renewsSoon ? (
                <span className="ml-2 badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
                  {t('seriesDetail.contract.renewsSoon')}
                </span>
              ) : null}
            </dd>
          </>
        ) : null}

        <dt className="text-[color:var(--color-muted)]">{t('seriesDetail.contract.autoRenew')}</dt>
        <dd>{contract.autoRenews ? t('seriesDetail.contract.yes') : t('seriesDetail.contract.no')}</dd>

        {contract.escalationBasisPoints !== null ? (
          <>
            <dt className="text-[color:var(--color-muted)]">{t('seriesDetail.contract.escalation')}</dt>
            <dd>{t('seriesDetail.contract.escalationPercent', { percent: contract.escalationBasisPoints / 100 })}</dd>
          </>
        ) : null}

        {contract.cancellationNoticeDays !== null ? (
          <>
            <dt className="text-[color:var(--color-muted)]">{t('seriesDetail.contract.notice')}</dt>
            <dd>{t('seriesDetail.contract.noticeDays', { count: contract.cancellationNoticeDays })}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}

function ruleStateFrom(series: AppointmentSeries): RuleFormState {
  return ruleStateFromSeries(series);
}

/** The page exists only on a plan that carries the calendar (plan-gate.tsx). */
export default function SeriesDetailPage() {
  const { t } = useTranslation();
  if (!usePlanFeature('scheduling')) return <PlanRequiredPage feature="scheduling" title={t('schedule.page.title')} />;
  return <SeriesDetailPageContent />;
}

function SeriesDetailPageContent() {
  const { t } = useTranslation();
  const { seriesId = '' } = useParams();
  const navigate = useNavigate();
  const {
    can,
    company,
    currentLocation,
    locations,
    switchLocation,
    user,
    currencyCode: sessionCurrency,
  } = useSession();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const editorRef = useRef<HTMLElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    if (editing) {
      const editor = editorRef.current;
      if (!editor) return;
      wasEditing.current = true;
      editor.focus({ preventScroll: true });
      const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      editor.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    } else if (wasEditing.current) {
      wasEditing.current = false;
      editButtonRef.current?.focus();
    }
  }, [editing]);
  const [confirmPause, setConfirmPause] = useState(false);
  const [rule, setRule] = useState<RuleFormState | null>(null);
  // A weekday price box holding text that is not an amount: Save waits for it.
  const [dayPriceInvalid, setDayPriceInvalid] = useState(false);
  const [billing, setBilling] = useState<BillingFormState>({ visitPriceCents: null });
  const [propertyId, setPropertyId] = useState('');
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  /**
   * Put the session on the schedule's branch before asking for the schedule.
   *
   * `GET /schedule/series/:id` is scoped to the session's location and
   * refuses one belonging to another branch (`assertSameLocation`), and the
   * mails that link here — a customer's booking above all — reach whoever is
   * nearest their laptop rather than whoever happens to be working that
   * branch today. Without this the link opens a page that can only say the
   * schedule may have been removed.
   *
   * Only to a branch this person already has, and the param is dropped once
   * the switch has happened so a refresh does not repeat it.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkLocationId = searchParams.get('location');
  useEffect(() => {
    if (!deepLinkLocationId) return;
    if (deepLinkLocationId === currentLocation?.id) {
      const next = new URLSearchParams(searchParams);
      next.delete('location');
      setSearchParams(next, { replace: true });
      return;
    }
    if (!locations.some((entry) => entry.id === deepLinkLocationId)) return;
    void switchLocation(deepLinkLocationId);
  }, [deepLinkLocationId, currentLocation?.id, locations, switchLocation, searchParams, setSearchParams]);

  const detail = useQuery({ queryKey: ['series', seriesId], queryFn: () => scheduleApi.series(seriesId) });
  const series = detail.data?.series;
  const visits = useSeriesVisits({
    seriesId,
    initial: detail.data?.upcoming ?? [],
    pagination: detail.data?.upcomingPagination,
    source: JSON.stringify([
      company?.id,
      user?.id,
      currentLocation?.id,
      seriesId,
      detail.dataUpdatedAt,
      series?.updatedAt,
      series?.horizonMode,
      series?.horizonUntil,
      series?.horizonCount,
      detail.data?.upcomingPagination?.snapshotAt,
      detail.data?.upcomingPagination?.snapshotToken,
    ]),
    refreshing: detail.isFetching,
    enabled:
      Boolean(series) && !(detail.error instanceof ApiRequestError && [401, 403, 404].includes(detail.error.status)),
  });

  useDocumentTitle(
    series
      ? t('seriesDetail.documentTitleWithCustomer', { customer: series.customerName })
      : t('seriesDetail.documentTitleFallback'),
  );

  const properties = useQuery({
    queryKey: ['customer-properties', series?.customerId],
    queryFn: () => customerApi.properties(series!.customerId),
    enabled: Boolean(series) && editing,
  });

  const team = useQuery({ queryKey: ['team'], queryFn: () => companyApi.users() });
  const checklistTemplates = useChecklistTemplateOptions();
  const assignedNames = (series?.assignedUserIds ?? [])
    .map((userId) => team.data?.items.find((member) => member.id === userId)?.fullName)
    .filter((name): name is string => Boolean(name));

  const mutationHandlers = {
    onSuccess: () => {
      setError(null);
      setFieldErrors({});
      setEditing(false);
      void detail.refetch();
      // Ending the schedule, or moving it, can give the quote's slot back.
      // The badge reads that count, not the stored Accepted status.
      invalidateQuoteViews(queryClient);
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiRequestError) {
        setFieldErrors(caught.fields);
        setError(Object.keys(caught.fields).length > 0 ? null : caught.message);
      } else {
        setError(t('seriesDetail.updateError'));
      }
    },
  };

  const save = useMutation({
    mutationFn: () =>
      scheduleApi.updateSeries(seriesId, {
        ...ruleToApiFields(rule!),
        propertyId: propertyId || null,
        assignedUserIds,
        ...(teamId !== series?.teamId ? { teamId } : {}),
        internalNotes: notes || null,
        visitPriceCents: billing.visitPriceCents,
      }),
    ...mutationHandlers,
  });
  const pause = useMutation({
    mutationFn: () => scheduleApi.pauseSeries(seriesId),
    onSuccess: (updatedSeries) => {
      queryClient.setQueryData<NonNullable<typeof detail.data>>(['series', seriesId], (current) =>
        current ? { ...current, series: updatedSeries } : current,
      );
      setConfirmPause(false);
      setError(null);
      void detail.refetch();
      void queryClient.invalidateQueries({ queryKey: ['appointments'] });
      // Pausing must not close an edit form holding unsaved changes.
    },
  });
  const resume = useMutation({ mutationFn: () => scheduleApi.resumeSeries(seriesId), ...mutationHandlers });
  const end = useMutation({ mutationFn: () => scheduleApi.endSeries(seriesId), ...mutationHandlers });
  /**
   * Drops the schedule's OWN automatic setting. Not a promise about charging.
   *
   * `autoBillOnCompletion` charges when the SERIES says automatic OR the
   * account does, so clearing the series half leaves the account half exactly
   * where it was: with automatic charging on in Settings → Payments, this
   * schedule's visits go on being charged, and correctly so. What it removes
   * is the schedule's ability to charge when the account says not to — which
   * is the state this page had no way out of once the field left the form.
   *
   * The wording has to carry that, which is why the button says what it
   * removes rather than what it stops. Deliberately one-way, too: nothing
   * sets `automatic` back, because that is the setting being retired.
   *
   * Its own success handler rather than the shared one. `mutationHandlers`
   * closes the editor, and this button sits ABOVE the editor in an alert the
   * reader can reach mid-edit — so sharing it would quietly throw away every
   * unsaved change on the form to apply a one-field PATCH that has nothing to
   * do with them.
   */
  const stopAutoBilling = useMutation({
    mutationFn: () => scheduleApi.updateSeries(seriesId, { billingMode: 'manual' }),
    onSuccess: () => {
      setError(null);
      void detail.refetch();
    },
    onError: mutationHandlers.onError,
  });

  if (detail.isLoading)
    return (
      <WorkPage title={t('schedule.page.title')}>
        <LoadingBlock label={t('seriesDetail.loading')} />
      </WorkPage>
    );

  const accessUnavailable = detail.error instanceof ApiRequestError && [401, 403, 404].includes(detail.error.status);
  if (!series || accessUnavailable) {
    return (
      <WorkPage title={t('schedule.page.title')}>
        <Alert tone="error" title={t('seriesDetail.loadErrorTitle')}>
          <p>{(detail.error as Error | null)?.message ?? t('seriesDetail.mayHaveBeenRemoved')}</p>
          <Button className="mt-3" busy={detail.isFetching} onClick={() => void detail.refetch()}>
            {t('common.retry')}
          </Button>
          <Link to="/schedule" className="mt-2 inline-block text-[color:var(--color-brand)] hover:underline">
            {t('seriesDetail.backToSchedule')}
          </Link>
        </Alert>
      </WorkPage>
    );
  }

  const upcoming = visits.items;
  const writable = can('schedule:write') && series.status !== 'ended';

  function startEditing() {
    if (!series) return;
    setRule(ruleStateFrom(series));
    setBilling({ visitPriceCents: series.visitPriceCents });
    setPropertyId(series.propertyId ?? '');
    setAssignedUserIds(series.assignedUserIds);
    setTeamId(series.teamId ?? null);
    setNotes(series.internalNotes ?? '');
    setEditing(true);
  }

  return (
    <WorkPage
      title={series.customerName}
      description={[
        describeRule(series),
        assignedNames.length > 0 ? t('seriesDetail.assignedTo', { name: assignedNames.join(', ') }) : null,
        // A schedule priced by weekday says so: "Mon $180 · Wed, Fri $120".
        dayPriceSummary(
          series.daySlots,
          series.visitPriceCents,
          (cents) =>
            cents === null
              ? t('seriesDetail.notBilled')
              : formatCents(
                  cents,
                  series.priceCurrencyCode ?? company?.currencyCode ?? DEFAULT_CURRENCY_CODE,
                  getDateLocale(),
                ),
          getDateLocale(),
        ) ??
          (series.visitPriceCents !== null
            ? // The price alone. How a visit is billed is an account setting
              // now (Settings → Payments), not a property of this schedule,
              // so repeating "billed manually" on every header would be
              // stating the default as though somebody had chosen it here.
              // The schedules where it IS a property of the schedule — the
              // legacy Automatic ones — say so in the note below instead,
              // where there is room to say what it means.
              t('seriesDetail.perVisit', {
                // The price's own currency, not the company's current one —
                // the same snapshot automatic billing reads, so the header
                // cannot claim a schedule is priced in money it never was.
                amount: formatCents(
                  series.visitPriceCents,
                  series.priceCurrencyCode ?? company?.currencyCode ?? DEFAULT_CURRENCY_CODE,
                  getDateLocale(),
                ),
              })
            : null),
      ]
        .filter(Boolean)
        .join(' · ')}
      actions={
        <>
          <span
            className={classes(
              'badge self-center',
              series.status === 'active'
                ? 'border-[color:var(--color-brand)] text-[color:var(--color-brand)]'
                : 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
            )}
          >
            {t(`schedule.seriesStatus.${series.status}`)}
          </span>
          {writable && !editing ? (
            <Button ref={editButtonRef} onClick={startEditing}>
              {t('seriesDetail.editSchedule')}
            </Button>
          ) : null}
          {writable && series.status === 'active' ? (
            <Button
              busy={pause.isPending}
              onClick={() => {
                pause.reset();
                setConfirmPause(true);
              }}
            >
              {t('seriesDetail.pause')}
            </Button>
          ) : null}
          {writable && series.status === 'paused' ? (
            <Button busy={resume.isPending} onClick={() => resume.mutate()}>
              {t('seriesDetail.resume')}
            </Button>
          ) : null}
          {writable ? (
            <Button
              variant="danger"
              busy={end.isPending}
              onClick={() => {
                if (window.confirm(t('seriesDetail.confirmEnd'))) end.mutate();
              }}
            >
              {t('seriesDetail.endSchedule')}
            </Button>
          ) : null}
        </>
      }
    >
      {detail.isError ? (
        <Alert tone="error" title={t('seriesDetail.loadErrorTitle')}>
          <p>{(detail.error as Error).message}</p>
          <Button className="mt-3" busy={detail.isFetching} onClick={() => void detail.refetch()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}

      {confirmPause ? (
        <ConfirmDialog
          title={t('seriesDetail.pauseTitle')}
          confirmLabel={t('seriesDetail.pause')}
          busy={pause.isPending}
          error={
            pause.error
              ? pause.error instanceof ApiRequestError
                ? pause.error.message
                : t('seriesDetail.updateError')
              : null
          }
          onConfirm={() => {
            if (!pause.isPending) pause.mutate();
          }}
          onCancel={() => setConfirmPause(false)}
        >
          <p>{t('seriesDetail.pauseBody')}</p>
          <p className="mt-3">{t('seriesDetail.pauseVisitHint')}</p>
        </ConfirmDialog>
      ) : null}

      <p className="mb-6 text-[color:var(--color-muted)]">
        <Link to={`/customers/${series.customerId}`} className="text-[color:var(--color-brand)] hover:underline">
          {t('seriesDetail.viewCustomer')}
        </Link>
        {series.quoteId ? (
          <>
            {' · '}
            <Link to={`/quotes/${series.quoteId}`} className="text-[color:var(--color-brand)] hover:underline">
              {t('seriesDetail.viewQuote')}
            </Link>
          </>
        ) : null}
        {' · '}
        <Link to="/schedule" className="text-[color:var(--color-brand)] hover:underline">
          {t('seriesDetail.backToCalendar')}
        </Link>
      </p>

      {error ? (
        <div className="mb-6">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {series.status === 'paused' ? (
        <div className="mb-6">
          <Alert tone="warning" title={t('seriesDetail.pausedTitle')}>
            {t('seriesDetail.pausedMessage')}
          </Alert>
        </div>
      ) : null}

      {(detail.data?.missingVisitDates.length ?? 0) > 0 ? (
        <div className="mb-6">
          <Alert tone="warning" title={t('seriesDetail.missingVisitsTitle')}>
            {t('seriesDetail.missingVisitsMessage', {
              dates: detail.data!.missingVisitDates.map(formatShortDate).join(' · '),
            })}
          </Alert>
        </div>
      ) : null}

      {/* A schedule carrying the per-schedule Automatic mode. The field is
          gone from this form — Settings → Payments carries the one switch
          now — but the column is still read, so this schedule goes on
          charging the card on completion exactly as it did. Said here because
          the form no longer has anywhere to say it, and a schedule that
          charges by itself for a reason nobody can see on screen is the one
          state this page must not leave silent.

          With a way to stop it: the account switch does not reach this,
          because completion charges when the SCHEDULE says automatic OR the
          account does. Without this button the only way off would be the
          API.

          Not on an ended schedule. `autoBillOnCompletion` ignores the stored
          mode once a schedule is ended, so the note's claim — that this
          charges whether or not the account switch is on — stops being true
          of it, and an ended schedule is also where `writable` takes the
          button away. A row ended before that rule shipped still reads
          `automatic`; what it says about itself is stale, not current. */}
      {series.billingMode === 'automatic' && series.status !== 'ended' ? (
        <div className="mb-6">
          <Alert tone="info" title={t('seriesDetail.legacyAutoBillTitle')}>
            <p>{t('seriesDetail.legacyAutoBillMessage')}</p>
            {writable ? (
              <Button
                className="mt-2"
                busy={stopAutoBilling.isPending}
                onClick={() => {
                  if (window.confirm(t('seriesDetail.confirmStopAutoBill'))) stopAutoBilling.mutate();
                }}
              >
                {t('seriesDetail.stopAutoBill')}
              </Button>
            ) : null}
          </Alert>
        </div>
      ) : null}

      {/* Below the alerts and above the editor: a rate change is a decision
          about this schedule, not a field on the form that changes when the
          next visit happens. Hidden on an ended schedule, which has no future
          visits to price. */}
      {series.status !== 'ended' ? (
        <PriceChangeCard series={series} writable={writable} onChanged={() => void detail.refetch()} />
      ) : null}

      {/* The agreement behind the schedule, when it carries one — which is
          commercial work booked from a quote with terms. */}
      {series.contract ? <ContractCard contract={series.contract} /> : null}

      {series.frequency !== 'one_time' ? (
        <HorizonCard
          key={series.id}
          series={series}
          writable={writable}
          onChanged={(next) => {
            queryClient.setQueryData(['series', seriesId], (current: typeof detail.data) =>
              current ? { ...current, series: next } : current,
            );
            void detail.refetch();
            void queryClient.invalidateQueries({ queryKey: ['schedule'] });
          }}
        />
      ) : null}

      {editing && rule ? (
        <section
          ref={editorRef}
          tabIndex={-1}
          className="card mb-6 scroll-mt-4"
          aria-label={t('seriesDetail.editAriaLabel')}
        >
          <h2 className="mb-4 text-lg font-semibold">{t('seriesDetail.editSchedule')}</h2>
          <Alert tone="info">{t('seriesDetail.editNotice')}</Alert>
          <div className="mt-4">
            <Select
              label={t('seriesDetail.propertyLabel')}
              value={propertyId}
              error={fieldErrors.propertyId}
              options={[
                { value: '', label: t('seriesDetail.noSpecificProperty') },
                ...(properties.data?.items ?? []).map((property) => ({
                  value: property.id,
                  label:
                    property.name ??
                    [property.addressLine1, property.city].filter(Boolean).join(', ') ??
                    t('seriesDetail.unnamedProperty'),
                })),
              ]}
              onChange={(event) => setPropertyId(event.target.value)}
            />
            <AssigneePicker
              label={t('seriesDetail.assigneeLabel')}
              value={assignedUserIds}
              teamId={teamId}
              onTeamChange={(id, members) => {
                setTeamId(id);
                setAssignedUserIds(members);
              }}
              onChange={setAssignedUserIds}
              error={fieldErrors.assignedUserIds ?? fieldErrors.assignedUserId}
            />
            {/* Rule first, then the calendar — the same order as the booking
                form, and for the same reason: the grid is drawn from this
                rule's length and repeat, and on a phone it is tall enough to
                bury anything placed under it. */}
            <RuleFields
              rule={rule}
              onChange={setRule}
              fieldErrors={fieldErrors}
              // The currency the save will stamp, as the server decides it: a
              // new schedule price is priced in what this location bills in now
              // (the price box above already is), and every weekday price
              // saved with it is stamped the same. Otherwise the weekdays
              // stay in the schedule's own currency.
              currencyCode={
                (billing.visitPriceCents !== series.visitPriceCents ? sessionCurrency : null) ??
                series.priceCurrencyCode ??
                sessionCurrency
              }
              checklistTemplates={checklistTemplates}
              onDayPriceInvalidChange={setDayPriceInvalid}
            />
            <AvailabilityPicker
              userIds={assignedUserIds}
              teamId={teamId}
              durationMinutes={
                firstVisitSelection(rule, todayInTimezone(series.timezone))?.durationMinutes ?? rule.durationMinutes
              }
              selected={firstVisitSelection(rule, todayInTimezone(series.timezone))}
              excludeSeriesId={seriesId}
              onPick={(date, time) => setRule((current) => (current ? ruleWithPick(current, date, time) : current))}
            />
            <BillingFields billing={billing} onChange={setBilling} fieldErrors={fieldErrors} />
            <TextArea
              label={t('seriesDetail.internalNotes')}
              tone="internal"
              rows={2}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              <Button variant="primary" busy={save.isPending} disabled={dayPriceInvalid} onClick={() => save.mutate()}>
                {t('seriesDetail.saveChanges')}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="card" aria-labelledby="visits-heading">
        <h2 id="visits-heading" className="mb-4 text-lg font-semibold">
          {t('seriesDetail.upcomingHeading')}
        </h2>

        {upcoming.length === 0 ? (
          <WorkEmpty title={t('seriesDetail.emptyTitle')}>
            <p>{series.status === 'active' ? t('seriesDetail.emptyActive') : t('seriesDetail.emptyInactive')}</p>
          </WorkEmpty>
        ) : (
          <ul className="divide-y divide-[color:var(--color-line)]">
            {upcoming.map((appointment) => (
              <li key={appointment.id}>
                <button
                  type="button"
                  className={classes(
                    'flex w-full cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg px-2 py-3 text-left',
                    'hover:bg-[color:var(--color-surface)]',
                    expandedId === appointment.id && 'bg-[color:var(--color-surface)]',
                  )}
                  onClick={() => setExpandedId(expandedId === appointment.id ? null : appointment.id)}
                  aria-expanded={expandedId === appointment.id}
                >
                  <span>
                    <span className="block font-semibold">
                      {formatShortDate(appointment.scheduledDate)} · {formatLocalTime(appointment.startTimeLocal)}
                      {appointment.scheduledDate !== appointment.occurrenceDate ? (
                        <span className="ml-2 text-sm font-normal text-[color:var(--color-muted)]">
                          {t('seriesDetail.movedFrom', { date: formatShortDate(appointment.occurrenceDate) })}
                        </span>
                      ) : null}
                    </span>
                    {appointment.confirmedByName ? (
                      <span className="block text-sm text-[color:var(--color-muted)]">
                        {t('seriesDetail.confirmedBy', { name: appointment.confirmedByName })}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex items-center gap-3">
                    {appointment.paymentStatus !== 'not_billed' ? (
                      <PaymentBadge status={appointment.paymentStatus} />
                    ) : null}
                    <AppointmentBadge status={appointment.status} />
                    <span className="whitespace-nowrap font-medium text-[color:var(--color-brand)]">
                      {expandedId === appointment.id ? t('schedule.page.hideManage') : t('schedule.page.manage')}{' '}
                      <span aria-hidden="true">{expandedId === appointment.id ? '▾' : '›'}</span>
                    </span>
                  </span>
                </button>
                {expandedId === appointment.id && can('schedule:write') ? (
                  <div className="mt-3">
                    <AppointmentActions appointment={appointment} onChanged={() => void detail.refetch()} />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {visits.snapshotSupported && detail.data?.upcomingPagination ? (
          <p className="mt-4 text-sm text-[color:var(--color-muted)]" aria-live="polite">
            {t('seriesDetail.visitsCount', { shown: upcoming.length, total: detail.data.upcomingPagination.total })}
          </p>
        ) : null}
        {visits.paginationUnavailable ? (
          <p className="mt-4 text-sm text-[color:var(--color-muted)]" aria-live="polite">
            {t('seriesDetail.visitsFirstPageOnly', { shown: upcoming.length })}
          </p>
        ) : null}
        {visits.hasMore && !visits.online ? (
          <p className="mt-3 text-sm text-[color:var(--color-muted)]">{t('seriesDetail.visitsOffline')}</p>
        ) : null}
        {visits.error ? (
          <div className="mt-3">
            <Alert tone="error">
              <p>{visits.error}</p>
              <Button
                className="mt-2"
                busy={visits.needsRefresh ? detail.isFetching : visits.loading}
                disabled={!visits.online || detail.isFetching}
                onClick={visits.needsRefresh ? () => void detail.refetch() : visits.loadMore}
              >
                {t(visits.needsRefresh ? 'seriesDetail.refreshVisits' : 'common.retry')}
              </Button>
            </Alert>
          </div>
        ) : null}
        {visits.hasMore && !visits.error ? (
          <Button
            className="mt-4"
            busy={visits.loading}
            disabled={!visits.online || detail.isFetching}
            onClick={visits.loadMore}
          >
            {t('seriesDetail.loadMoreVisits')}
          </Button>
        ) : null}
      </section>

      {series.status === 'ended' ? (
        <p className="mt-6">
          <Button onClick={() => navigate(`/schedule/new?customerId=${series.customerId}`)}>
            {t('seriesDetail.startNewSchedule')}
          </Button>
        </p>
      ) : null}
    </WorkPage>
  );
}
